import { useId } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import PrimaryButton from "@/components/common/PrimaryButton"
import SecondaryButton from "@/components/common/SecondaryButton"
import BottomSheet from "@/components/bottom-sheet"
import { createCurrencyFormatter, formatMoney } from "@/utils/locale-format"
import type { ActiveUnpaidOrderConflict } from "@/domain/order/active-unpaid-order"
import { useTenantBranding } from "@/hooks/use-tenant-branding"
import { primaryActionSx } from "@/theme/primary-action"
import { tenantActionCtaSx } from "@/theme/tenant-cta-sx"

interface ActiveUnpaidOrderSheetProps {
  open: boolean
  /** When null/undefined, informational rows are omitted (fallback lock without orderId). */
  conflict?: ActiveUnpaidOrderConflict | null
  onContinuePayment: () => void
  onViewOrder: () => void
  onClose: () => void
  /** Defaults to checkout-conflict copy. */
  title?: string
  message?: string
  /** Hide Continue payment (e.g. locked without a resolvable orderId). */
  hideContinuePayment?: boolean
  /** Label for the secondary order navigation action. */
  viewOrderLabel?: string
  /** When false, only Dismiss remains (besides optional Continue). Default true. */
  showViewOrder?: boolean
}

function InfoRow({ label, value }: { label: string; value: string }) {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 2,
        minWidth: 0,
      }}
    >
      <Typography
        component="dt"
        sx={{
          m: 0,
          fontSize: theme.typography.pxToRem(13),
          color: theme.palette.text.secondary,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{
          m: 0,
          fontSize: theme.typography.pxToRem(13),
          fontWeight: 500,
          color: theme.palette.text.primary,
          textAlign: "right",
          minWidth: 0,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

/**
 * Gate UI for ACTIVE_UNPAID_ORDER_EXISTS (4091) and cart-lock recovery
 * (including fallback when locked without a resolvable orderId).
 */
export default function ActiveUnpaidOrderSheet({
  open,
  conflict = null,
  onContinuePayment,
  onViewOrder,
  onClose,
  title = "Checkout unavailable",
  message = "You already have an order awaiting payment. Complete or cancel that order before placing a new order.",
  hideContinuePayment = false,
  viewOrderLabel = "View order",
  showViewOrder = true,
}: ActiveUnpaidOrderSheetProps) {
  const theme = useTheme()
  const titleId = useId()
  const { primaryTokens: continueActionTokens } = useTenantBranding()
  const continueCtaSx = continueActionTokens
    ? tenantActionCtaSx(continueActionTokens)
    : primaryActionSx

  const amountLabel =
    conflict?.payableAmount != null
      ? conflict.currencyCode
        ? createCurrencyFormatter(conflict.currencyCode)(conflict.payableAmount)
        : formatMoney(conflict.payableAmount)
      : null

  const orderNumber = conflict?.orderNumber?.trim() || null
  const paymentStatus = conflict?.paymentStatus?.trim() || null

  const header = (
    <Typography
      id={titleId}
      component="h2"
      sx={{
        m: 0,
        fontSize: "1.125rem",
        fontWeight: 600,
        color: theme.palette.text.primary,
      }}
    >
      {title}
    </Typography>
  )

  const footer = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
      {!hideContinuePayment ? (
        <PrimaryButton
          size="large"
          fullWidth
          onClick={onContinuePayment}
          aria-label="Continue payment"
          data-tenant-primary={continueActionTokens?.fill}
          sx={continueCtaSx}
        >
          Continue payment
        </PrimaryButton>
      ) : null}
      {showViewOrder ? (
        <SecondaryButton
          size="large"
          fullWidth
          onClick={onViewOrder}
          aria-label={viewOrderLabel}
        >
          {viewOrderLabel}
        </SecondaryButton>
      ) : null}
      <Box
        component="button"
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        sx={{
          appearance: "none",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          alignSelf: "center",
          px: 1.5,
          py: 0.75,
          minHeight: 40,
          fontFamily: "inherit",
          fontSize: theme.typography.pxToRem(14),
          fontWeight: 500,
          color: theme.palette.text.secondary,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          "&:hover": {
            color: theme.palette.text.primary,
          },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
            borderRadius: 1,
          },
        }}
      >
        Dismiss
      </Box>
    </Box>
  )

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      header={header}
      footer={footer}
      maxHeight={85}
      ariaLabelledBy={titleId}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography
          sx={{
            m: 0,
            fontSize: theme.typography.pxToRem(14),
            lineHeight: 1.45,
            color: theme.palette.text.secondary,
          }}
        >
          {message}
        </Typography>

        {orderNumber || amountLabel || paymentStatus ? (
          <Box
            component="dl"
            sx={{
              m: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minWidth: 0,
            }}
          >
            {orderNumber ? <InfoRow label="Order number" value={`#${orderNumber}`} /> : null}
            {amountLabel ? <InfoRow label="Amount due" value={amountLabel} /> : null}
            {paymentStatus ? <InfoRow label="Payment status" value={paymentStatus} /> : null}
          </Box>
        ) : null}
      </Box>
    </BottomSheet>
  )
}
